import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { Download } from 'react-bootstrap-icons';
import { fetchTournamentData, getMatchBoardForTournament, IMatchBoardItem } from '../../apis/tournament';
import { BackIcon } from '../../assets/icons/BackIcon';
import { RaqtTable, Switcher } from '../../components';
import { IRaqtTableConfig } from '../../components/table/RaqtTable';
import { fetchTournamentClassListPure } from '../../features/class/classActions';
import { selectClassList } from '../../features/class/classSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { TournamentMatchStatusEnumTranslation } from '../../types/enums';
import { formatVenueDateWithTime } from '../../utils/tournamentDateTime';
import { getTournamentTimezone } from '../tourDetails/Schedule/components/schedule.helpers';
import './TournamentMatchesStats.scss';


export const TournamentMatchesStats = () => {
    const prefix = 'tournamentStatsMatches-';

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [matchList, setMatchList] = useState<(Omit<IMatchBoardItem, 'results' | 'status'> & { results: string, status: string })[]>([]);
    // const [isLoading, setIsloading] = useState(false);
    const [classId, setClassId] = useState<number | null>(null);
    const [tournamentName, setTournamentName] = useState('');
    const [venueTimeZone, setVenueTimeZone] = useState('UTC');
    const { id , communityId } = useParams<{ id: string, communityId: string }>();
    const classes = useAppSelector(selectClassList);

    const classList = [
        {label: 'All', value: null},
        ...classes.map(({classId, name}) => ({
            value: classId,
            label: name,
        }))
    ];

    const getMatches = async () => {
        // setIsloading(true);

        try {
            const [{matchBoard}, tournamentData] = await Promise.all([
                getMatchBoardForTournament({id: +id, classId}),
                !tournamentName ? fetchTournamentData(+id) : Promise.resolve(null),
            ]);
            const timeZone = tournamentData
                ? getTournamentTimezone(tournamentData.country, tournamentData.timeZone)
                : venueTimeZone;
            setMatchList(matchBoard.map(i => ({
                ...i,
                startDate: formatVenueDateWithTime(i.startDate, timeZone),
                status: t(TournamentMatchStatusEnumTranslation[i.status]),
                results: i.results.filter(Boolean).join(', ')
            })));
            if (tournamentData) {
                setTournamentName(tournamentData.name);
                setVenueTimeZone(timeZone);
            }
        } catch (e) {
            setMatchList([]);
        }

        // setIsloading(false);
    }

    const downloadData = useMemo(() => matchList.map(m => ({
        matchIdToShow: m.matchIdToShow,
        tournamentClassName: m.tournamentClassName,
        startDate: m.startDate,
        courtName: m.courtName,
        homeTeamName: m.homeTeamName,
        homePlayer1: m.homePlayer1,
        homePlayer2: m.homePlayer2 ?? '',
        awayTeamName: m.awayTeamName,
        awayPlayer1: m.awayPlayer1,
        awayPlayer2: m.awayPlayer2 ?? '',
        results: m.results,
        status: m.status,
    })), [matchList]);

    const downloadFilename = useMemo(() => {
        const className = classes.find(c => c.classId === classId)?.name;
        return classId !== null && className
            ? `Matches - ${tournamentName} - ${className}`
            : `Matches - ${tournamentName}`;
    }, [classId, classes, tournamentName]);

    const tableConfig: IRaqtTableConfig = {
        blankHead: true,
        columns: [
            {
                label: t(prefix + 'class').toLocaleUpperCase(),
                key: 'tournamentClassName',
                position: 'left',
            },
            {
                label: t(prefix + 'startDate').toLocaleUpperCase(),
                key: 'startDate',
                columnSize: 'small',
                position: 'left',
            },
            {
                label: t(prefix + 'firstTeam').toLocaleUpperCase(),
                key: 'homeTeamName',
                position: 'left',
            },
            {
                label: t(prefix + 'secondTeam').toLocaleUpperCase(),
                key: 'awayTeamName',
                position: 'left',
            },
            {
                label: t(prefix + 'results').toLocaleUpperCase(),
                key: 'results',
                columnSize: 'small',
                position: 'left',
            },
            {
                label: t(prefix + 'status').toLocaleUpperCase(),
                key: 'status',
                columnSize: 'small',
                position: 'left',
            }
        ]
    }

    useEffect(() => {
        getMatches();
        !classes.length && dispatch(fetchTournamentClassListPure(+id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, classId, classes]) 
    
    return <div className="tournament-matches-stats">
        <div className="tournament-matches-stats__title">{t(prefix + 'title')}</div>
        <div className="tournament-matches-stats__back-link">
            <NavLink to={`/community/${communityId}/tournament/${id}/tags/dashboard`} >
                <BackIcon w="10" h="16" f="#0474EF" /> <span className="tournament-matches-stats__back-text">{t('tournamentDashboard-backButton')}</span>
            </NavLink>
        </div>
        <Switcher switches={classList} onSelect={setClassId} selectedValue={classId} />
        <CSVLink
            data={downloadData}
            headers={[
                { label: 'Match ID', key: 'matchIdToShow' },
                { label: 'Class', key: 'tournamentClassName' },
                { label: 'Date', key: 'startDate' },
                { label: 'Court', key: 'courtName' },
                { label: 'Home Team', key: 'homeTeamName' },
                { label: 'Home Player 1', key: 'homePlayer1' },
                { label: 'Home Player 2', key: 'homePlayer2' },
                { label: 'Away Team', key: 'awayTeamName' },
                { label: 'Away Player 1', key: 'awayPlayer1' },
                { label: 'Away Player 2', key: 'awayPlayer2' },
                { label: 'Result', key: 'results' },
                { label: 'Status', key: 'status' },
            ]}
            filename={`${downloadFilename}.csv`}
            className="btn btn-primary mt-4"
            target="_blank"
        >
            <Download className="mr-2 mb-1" size={16} />
            {classId === null
                ? t(prefix + 'downloadTournamentMatches')
                : t(prefix + 'downloadClassMatches')}
        </CSVLink>
        <div className="tournament-matches-stats__table-holder">
            <RaqtTable config={tableConfig} data={matchList} />
        </div>
    </div>;
}